import React, { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";

import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import { TRACKING_ID } from "./data/tracking";
import "./App.css";
import "./components/common/styles/skeleton.css";

// Public pages — each chunk loaded on demand
const Homepage      = lazy(() => import("./pages/homepage"));
const ExtraTime     = lazy(() => import("./pages/extra-time"));
const Research      = lazy(() => import("./pages/research"));
const Teaching      = lazy(() => import("./pages/teaching"));
const Blog          = lazy(() => import("./pages/blog"));
const BlogPost      = lazy(() => import("./pages/blog-post"));
const CvPage        = lazy(() => import("./pages/cv"));
const Notfound      = lazy(() => import("./pages/404"));

// Admin pages — separate chunk loaded only when visiting /hq
const Login         = lazy(() => import("./admin/Login"));
const Dashboard     = lazy(() => import("./admin/Dashboard"));
const PageEditor    = lazy(() => import("./admin/PageEditor"));
const BlogManager   = lazy(() => import("./admin/BlogManager"));
const BlogPostEditor= lazy(() => import("./admin/BlogPostEditor"));
const FeedManager   = lazy(() => import("./admin/FeedManager"));
const MediaManager  = lazy(() => import("./admin/MediaManager"));
const Profile       = lazy(() => import("./admin/Profile"));

function PageSpinner() {
	return (
		<div className="page-spinner">
			<div className="page-spinner-icon" aria-label="Loading…" role="status" />
		</div>
	);
}

function RequireAuth({ children }) {
	const { user, loading } = useAdminAuth();
	const location = useLocation();

	if (loading) return <PageSpinner />;
	if (!user) return <Navigate to="/hq/login" state={{ from: location }} replace />;
	return children;
}

function App() {
	useEffect(() => {
		if (TRACKING_ID !== "") {
			ReactGA.initialize(TRACKING_ID);
		}
	}, []);

	return (
		<AdminAuthProvider>
			<div className="App">
				<Suspense fallback={<PageSpinner />}>
					<Routes>
						{/* Public site */}
						<Route path="/"            element={<Homepage />} />
						<Route path="/about"       element={<Navigate to="/#journey" replace />} />
						<Route path="/extra-time"  element={<ExtraTime />} />
						<Route path="/about/extra-time" element={<Navigate to="/extra-time" replace />} />
						<Route path="/research"    element={<Research />} />
						<Route path="/teaching"    element={<Teaching />} />
						<Route path="/blog"        element={<Blog />} />
						<Route path="/blog/:slug"  element={<BlogPost />} />
						<Route path="/cv"          element={<CvPage />} />

						{/* /admin disabled — show 404 rather than leaking the real path */}
						<Route path="/admin"   element={<Notfound />} />
						<Route path="/admin/*" element={<Notfound />} />

						{/* Admin — /hq */}
						<Route path="/hq/login" element={<Login />} />
						<Route path="/hq" element={<RequireAuth><Dashboard /></RequireAuth>} />
						<Route path="/hq/pages" element={<RequireAuth><PageEditor /></RequireAuth>} />
						<Route path="/hq/pages/:slug" element={<RequireAuth><PageEditor /></RequireAuth>} />
						<Route path="/hq/blog" element={<RequireAuth><BlogManager /></RequireAuth>} />
						<Route path="/hq/blog/new" element={<RequireAuth><BlogPostEditor /></RequireAuth>} />
						<Route path="/hq/blog/:id/edit" element={<RequireAuth><BlogPostEditor /></RequireAuth>} />
						<Route path="/hq/feeds" element={<RequireAuth><FeedManager /></RequireAuth>} />
						<Route path="/hq/media" element={<RequireAuth><MediaManager /></RequireAuth>} />
						<Route path="/hq/profile" element={<RequireAuth><Profile /></RequireAuth>} />

						<Route path="*" element={<Notfound />} />
					</Routes>
				</Suspense>
			</div>
		</AdminAuthProvider>
	);
}

export default App;
